import type {
	FormSchema,
	FormSchemaIssue,
	FormSchemaResult,
} from '$global/utils/form-utils/types';
import { validateEmail } from '$global/utils/validateEmail/validateEmail';

export const EmptyEmailErrorMessage = 'Please provide an email address.';
export const InvalidEmailErrorMessage = 'Please provide a valid email address.';

type NewsletterValues = {
	email: string;
	firstname?: string;
	surname?: string;
};

// zod 3's `z.string().email()` pattern. This schema replaced a zod one, and
// keeping zod's pattern next to `validateEmail` keeps exactly which addresses
// pass, and the error reported for each, unchanged.
const ZOD_EMAIL_PATTERN =
	/^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;

const typeIssue = (field: string, value: unknown): FormSchemaIssue => ({
	path: [field],
	code: 'invalid_type',
	message:
		value === undefined
			? 'Required'
			: `Expected string, received ${value === null ? 'null' : typeof value}`,
});

const emailIssues = (email: unknown): FormSchemaIssue[] => {
	if (typeof email !== 'string') {
		return [typeIssue('email', email)];
	}

	// Every check runs and each failure is reported, in zod's order; createForm
	// shows the last one for the field, the sign-up widget the first.
	const issues: FormSchemaIssue[] = [];

	if (!ZOD_EMAIL_PATTERN.test(email)) {
		issues.push({
			path: ['email'],
			code: 'invalid_string',
			validation: 'email',
			message: InvalidEmailErrorMessage,
		});
	}

	if (email.length < 1) {
		issues.push({
			path: ['email'],
			code: 'too_small',
			message: EmptyEmailErrorMessage,
		});
	}

	if (!email || !validateEmail(email)) {
		issues.push({
			path: ['email'],
			code: 'custom',
			message: InvalidEmailErrorMessage,
		});
	}

	return issues;
};

const optionalStringIssues = (field: string, value: unknown) =>
	value === undefined || typeof value === 'string'
		? []
		: [typeIssue(field, value)];

/**
 * Validates the newsletter sign-up fields without zod.
 *
 * WHY: the newsletter form is in the footer of every page, so the zod schema it
 * used put zod (~54 KiB) in every page's preloaded scripts. This implements the
 * `FormSchema` shape createForm reads, with the same rules, messages and issue
 * codes (analytics `error_type`) as the zod schema it replaces.
 */
export const NewsletterValidationSchema = {
	shape: {
		email: { isOptional: () => false },
		firstname: { isOptional: () => true },
		surname: { isOptional: () => true },
	},
	safeParse: (values: unknown): FormSchemaResult => {
		const { email, firstname, surname } = (values ?? {}) as Record<
			string,
			unknown
		>;
		const issues = [
			...emailIssues(email),
			...optionalStringIssues('firstname', firstname),
			...optionalStringIssues('surname', surname),
		];

		return issues.length
			? { success: false, error: { issues } }
			: { success: true };
	},
	_output: undefined as unknown as NewsletterValues,
} satisfies FormSchema;
